import React, { useState } from 'react';
import RiskHeroCard from './status/RiskHeroCard';
import WindMapCard from './status/WindMapCard';
import SevenDayTrend from './status/SevenDayTrend';
import CommunityReports from './status/CommunityReports';
import RiskLevelsModal from './status/RiskLevelsModal';

const HOW_RISK_ITEMS = [
  ['Cyanobacteria levels set the baseline risk', 'The buoy continuously measures cyanobacteria using phycocyanin fluorescence. Higher levels mean higher probability that toxins are present — a relationship well-established across decades of research.'],
  ['Water conditions refine the estimate', 'Water temperature, dissolved oxygen at multiple depths, chlorophyll-a, and turbidity reveal whether conditions favor toxic bloom species. Warm, stratified water with low bottom oxygen is a red flag.'],
  ['Trends catch trouble early — and after the peak', 'The model watches how fast levels are changing. A bloom that doubles in 48 hours raises the risk level before the readings alone would. And when a bloom dies off, the dying cells release their toxins — so the model keeps risk raised for a week or more after a peak, even as the water starts to look clearer.'],
  ['Wind predicts where scum concentrates', 'Wind speed and direction tell us where cyanobacteria are accumulating. Calm conditions let scums form anywhere. Steady wind pushes them to the downwind shore.'],
  ['Community reports fill in the full picture', 'Photo-verified scum reports catch things the center-pond buoy can\'t — patchy shoreline scums, species that don\'t fluoresce strongly, and conditions that shift with the wind.'],
  ['Lab results are the gold standard', 'When available, lab testing for species and toxin concentrations directly confirms or adjusts the sensor-based estimate. We weight lab data by recency.'],
];

function Expanders({ setShowRiskLevels }) {
  const [showHow, setShowHow] = useState(false);

  const chevron = (open) => (
    <span style={{
      fontSize: 14, color: '#6f6a5f', display: 'inline-block',
      transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .2s',
    }}>›</span>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button
        onClick={() => setShowRiskLevels(true)}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          width: '100%', padding: '14px 18px', borderRadius: 12,
          background: '#fff', border: '1px solid #ece7db',
          fontSize: 15, fontWeight: 600, color: '#1f2a2e',
          cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
        }}
      >
        What do these risk levels mean?
        {chevron(false)}
      </button>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ece7db', overflow: 'hidden' }}>
        <button
          onClick={() => setShowHow(v => !v)}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            width: '100%', padding: '14px 18px', background: 'none', border: 'none',
            fontSize: 15, fontWeight: 600, color: '#1f2a2e',
            cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
          }}
        >
          How is this risk estimated?
          {chevron(showHow)}
        </button>
        {showHow && (
          <div style={{ padding: '0 18px 18px' }}>
            <p style={{ fontSize: 15, color: '#46505a', lineHeight: 1.65, marginBottom: 14, marginTop: 0 }}>
              Think of this like a <strong>weather forecast for your lake</strong> — useful for planning, but always check the actual conditions when you arrive.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {HOW_RISK_ITEMS.map(([title, body], i) => (
                <div key={i} style={{ padding: '12px 14px', borderRadius: 9, background: '#f8f7f4' }}>
                  <strong style={{ fontSize: 14, color: '#1e293b', display: 'block', marginBottom: 4 }}>{title}</strong>
                  <p style={{ fontSize: 14, color: '#46505a', lineHeight: 1.6, margin: 0 }}>{body}</p>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 9, background: '#f8f7f4', border: '1px solid #e8e4df' }}>
              <strong style={{ fontSize: 14, color: '#1e293b', display: 'block', marginBottom: 4 }}>This is an early version of our model.</strong>
              <p style={{ fontSize: 14, color: '#46505a', lineHeight: 1.6, margin: 0 }}>We're actively comparing these estimates against lab-tested water samples and tuning the model as we learn. The scum sightings you report help too — they teach the model what conditions actually look like at the shoreline. Expect it to keep improving, and in the meantime, your eyes are an important part of the picture. When in doubt, stay out.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CurrentStatusTab({
  tierKey, pcTrend, habRisk, habRisk7Day, loadingHabRisk, transformedData, latestLatLon, gridData, isAdmin, demoMode, rangeLabel,
  haveInternalWind, haveExternalWind,
}) {
  const [showRiskLevels, setShowRiskLevels] = useState(false);
  const [windExpanded, setWindExpanded] = useState(false);

  const isElevated = tierKey !== 'BASELINE';

  const windCard = isElevated ? (
    <WindMapCard
      transformedData={transformedData}
      latestLatLon={latestLatLon}
      gridData={gridData}
      demoMode={demoMode}
      rangeLabel={rangeLabel}
      haveInternalWind={haveInternalWind}
      haveExternalWind={haveExternalWind}
      habRisk={habRisk}
    />
  ) : (
      <div style={{ background: '#fff', border: '1px solid #ece7db', borderRadius: 16, marginBottom: 16, overflow: 'hidden' }}>
        <button
          onClick={() => setWindExpanded(v => !v)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
        >
          <div>
            <span style={{ fontSize: 15, fontWeight: 600, color: '#46505a' }}>Wind & lake conditions</span>
            <span style={{ fontSize: 14, color: '#6f6a5f', marginLeft: 10 }}>Less relevant at low risk</span>
          </div>
          <span style={{ fontSize: 14, color: '#6f6a5f', display: 'inline-block', transform: windExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>▾</span>
        </button>
        {windExpanded && (
          <>
            <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 20px 12px', lineHeight: 1.5 }}>
              Wind matters most during a bloom, when it pushes surface scum toward downwind shores. Shown here for reference.
            </p>
            <WindMapCard
              transformedData={transformedData}
              latestLatLon={latestLatLon}
              gridData={gridData}
              rangeLabel={rangeLabel}
              haveInternalWind={haveInternalWind}
              haveExternalWind={haveExternalWind}
              habRisk={habRisk}
            />
          </>
        )}
      </div>
  );

  return (
    <div style={{ paddingBottom: 40 }}>
      {showRiskLevels && <RiskLevelsModal onClose={() => setShowRiskLevels(false)} />}

      {isElevated ? (
        /* ── Elevated+: two-column layout ───────────────────────────── */
        <div
          className="cs-elevated-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
            gap: 20,
            alignItems: 'start',
          }}
        >
          <div>
            <RiskHeroCard tierKey={tierKey} pcTrend={pcTrend} habRisk={habRisk} gridData={gridData} />
            <SevenDayTrend habRisk7Day={habRisk7Day} loading={loadingHabRisk} transformedData={transformedData} currentTierKey={tierKey} />
            {windCard}
          </div>
          <div>
            <CommunityReports tierKey={tierKey} onReport={() => {}} />
            <Expanders setShowRiskLevels={setShowRiskLevels} />
          </div>
        </div>
      ) : (
        /* ── Baseline: single reading column (max 820px, left-anchored) */
        <div style={{ maxWidth: 820 }}>
          <RiskHeroCard tierKey={tierKey} pcTrend={pcTrend} habRisk={habRisk} gridData={gridData} />
          <SevenDayTrend habRisk7Day={habRisk7Day} transformedData={transformedData} currentTierKey={tierKey} />
          {windCard}
          <CommunityReports tierKey={tierKey} onReport={() => {}} />
          <Expanders setShowRiskLevels={setShowRiskLevels} />
        </div>
      )}
    </div>
  );
}
